import React, { useEffect, useState } from 'react';
import { Button, Card, Checkbox, Col, Flex, Form, Input, Modal, Popconfirm, Row, Space } from 'antd';
import { EditOutlined, RobotOutlined } from '@ant-design/icons';
import InputNumber from 'components/input-number';
import ImagePicker from 'components/pickers/image/MultiImagePicker';
import MultiYoutubeVideoPicker from 'components/pickers/youtube-video/MultiYoutubeVideoPicker';
import TagsPicker from 'components/pickers/product_tag';
import CategoryPicker from 'components/pickers/product_category';
import useVariantComponent from '../variant/useComponent';
import VariantBlockDesktop from './components/VariantBlockDesktop';
import usePriceComponent from '../price/useComponent';
import PriceBlockDesktop from './components/PriceBlockDesktop';
import { useWorkSpace } from 'contexts/workspace';
import { useApp } from 'contexts/app';

const InventoryRowEdit = ({ row, showTitle, onChange, onChangeAll }) => {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(0);

  useEffect(() => {
    setQty(row?.on_hand_quantity || 0);
  }, [row?.on_hand_quantity]);

  const handleOk = () => {
    setOpen(false);
    onChange(qty);
  };

  const handleApplyAll = () => {
    setOpen(false);
    onChangeAll(qty);
  };

  return (
    <Card size='small' type='inner'>
      <Flex justify='space-between' align='center' gap={12}>
        {showTitle && <div style={{ fontWeight: 500 }}>{row.variant_title}</div>}
        <Space size={16}>
          <div>Tồn: <b>{row.on_hand_quantity}</b></div>
          <Button type='text' size='small' icon={<EditOutlined />} onClick={() => setOpen(true)} />
        </Space>
      </Flex>

      <Modal
        title='Cập nhật tồn kho'
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <Space>
            {onChangeAll && <Button type='primary' onClick={handleApplyAll}>Áp dụng chung</Button>}
            <Button type='primary' onClick={handleOk}>Áp dụng</Button>
            <Button onClick={() => setOpen(false)}>Hủy</Button>
          </Space>
        }
      >
        <Space direction='vertical' size={8}>
          {showTitle && <div><b>{row.variant_title}</b></div>}
          <div>Số lượng tồn kho:</div>
          <InputNumber value={qty} onChange={(v) => setQty(v)} min={0} />
        </Space>
      </Modal>
    </Card>
  );
};

const Desktop = ({ viewModel }) => {
  const { workspace } = useWorkSpace();
  const { message } = useApp();
  const {
    id,
    form,
    inventoryRows,
    draftOptions,
    draftVariants,
    saving,
    showAdvanced,
    setShowAdvanced,
    onFinish,
    deleteProduct,
    copyProduct,
    onDraftVariantStateChange,
    onDraftPriceChange,
    onVariantStateChange,
    onPriceStateChange,
    onInventoryChange,
    onInventoryChangeAll,
    generatingDescription,
    generateDescriptionByAI,
  } = viewModel;

  const handleViewProductWebsite = () => {
    const productID = String(id || '').trim();
    if (!productID) {
      message.error('Sản phẩm chưa được tạo để xem trên website');
      return;
    }

    const websiteBaseUrl = String(workspace?.websiteUrl || '').replace(/\/$/, '');
    if (!websiteBaseUrl) {
      message.error('Chưa cấu hình website shop để xem sản phẩm');
      return;
    }

    window.open(`${websiteBaseUrl}/product/${encodeURIComponent(productID)}`, '_blank', 'noopener,noreferrer');
  };
  const variantViewModel = useVariantComponent({
    id,
    draftMode: !id,
    draftOptions,
    draftVariants,
    onDraftChange: onDraftVariantStateChange,
    onStateChange: onVariantStateChange,
  });
  const priceViewModel = usePriceComponent({
    id,
    draftMode: !id,
    draftVariants,
    onDraftVariantsChange: onDraftPriceChange,
    onStateChange: onPriceStateChange,
  });

  return (
    <Form
      form={form}
      layout='vertical'
      initialValues={{
        track_inventory: false,
        allow_backorder: true,
      }}
      onFinish={onFinish}
      autoComplete='off'
    >
      <Space direction='vertical' size={16} style={{ width: '100%', paddingBottom: 64 }}>
        <Row gutter={16} align='top'>
          <Col span={24}>
            <Space direction='vertical' size={16} style={{ width: '100%' }}>
              <Card title='Thông tin cơ bản'>
                <Form.Item hidden name='id' />
                <Form.Item label='Tên sản phẩm' name='name' rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm' }]}>
                  <Input placeholder='Ví dụ: Trà đào cam sả' />
                </Form.Item>
                <Form.Item label='Hình ảnh' name='images' rules={[{ required: true, message: 'Vui lòng thêm hình ảnh' }]}>
                  <ImagePicker />
                </Form.Item>
                <Form.Item label='Danh mục' name='category'>
                  <CategoryPicker />
                </Form.Item>
                <Form.Item label='Tags' name='tags'>
                  <TagsPicker mode='multiple' />
                </Form.Item>
                <Form.Item label='Mô tả' name='description' rules={[{ required: true, message: 'Vui lòng nhập mô tả sản phẩm' }]}>
                  <Input.TextArea rows={6} placeholder='Mô tả ngắn gọn, dễ đọc cho khách hàng' />
                </Form.Item>
                <Space style={{ marginTop: -8, marginBottom: 12 }}>
                  <Button icon={<RobotOutlined />} loading={generatingDescription} onClick={generateDescriptionByAI}>
                    Tạo mô tả bằng AI
                  </Button>
                </Space>
                <Form.Item name='show_on_website' valuePropName='checked' style={{ marginBottom: 0 }}>
                  <Checkbox>Hiển thị trên website</Checkbox>
                </Form.Item>
              </Card>

              <Card title='Biến thể'>
                <VariantBlockDesktop viewModel={variantViewModel} />
              </Card>

              <Card title='Giá'>
                <PriceBlockDesktop viewModel={priceViewModel} hasMultipleVariants={variantViewModel.hasMultipleVariants} />
              </Card>

              <Card title='Kho'>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name='track_inventory' valuePropName='checked'>
                      <Checkbox>Bật theo dõi tồn kho</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name='allow_backorder' valuePropName='checked'>
                      <Checkbox>Cho phép đặt vượt tồn</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                {id || draftVariants.length > 0 ? (
                  <Space direction='vertical' size={8} style={{ width: '100%' }}>
                    {inventoryRows.map((row) => (
                      <InventoryRowEdit
                        key={row.id || row.variant_id}
                        row={row}
                        showTitle={variantViewModel.hasMultipleVariants}
                        onChange={(qty) => onInventoryChange(row.id || row.variant_id, qty)}
                        onChangeAll={variantViewModel.hasMultipleVariants ? onInventoryChangeAll : undefined}
                      />
                    ))}
                    {inventoryRows.length === 0 && <div style={{ color: '#8c8c8c' }}>Chưa có dữ liệu tồn kho.</div>}
                  </Space>
                ) : (
                  <div style={{ color: '#8c8c8c' }}>Tạo biến thể để hiển thị dữ liệu kho.</div>
                )}
              </Card>

              <Card
                title='Thông tin mở rộng'
                extra={<Button type='link' onClick={() => setShowAdvanced(!showAdvanced)}>{showAdvanced ? 'Thu gọn' : 'Mở rộng'}</Button>}
              >
                {showAdvanced ? (
                  <Space direction='vertical' size={0} style={{ width: '100%' }}>
                    <Form.Item label='Youtube videos' name='videos'>
                      <MultiYoutubeVideoPicker />
                    </Form.Item>
                    <Form.Item label='Shopee link' name='shopee_link'>
                      <Input />
                    </Form.Item>
                    <Form.Item label='Tiktok shop link' name='tiktok_shop_link'>
                      <Input />
                    </Form.Item>
                  </Space>
                ) : (
                  <div style={{ color: '#8c8c8c' }}>Ẩn bớt các trường ít dùng để tập trung vào luồng nhập liệu chính.</div>
                )}
              </Card>
            </Space>
          </Col>
        </Row>
        {id && (
          <Space>
            <Button onClick={handleViewProductWebsite}>Xem trên website</Button>
            <Popconfirm title='Copy sản phẩm' description='Bạn có chắc chắn muốn tạo bản sao sản phẩm này?' onConfirm={copyProduct}>
              <Button>Copy sản phẩm</Button>
            </Popconfirm>
            <Popconfirm title='Xoá sản phẩm' description='Bạn có chắc chắn muốn xoá sản phẩm này?' onConfirm={deleteProduct}>
              <Button danger>Xoá sản phẩm</Button>
            </Popconfirm>
          </Space>
        )}
      </Space>

      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          padding: '10px 24px',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
        }}
      >
        <Flex justify='flex-end' gap={8}>
          <Button type='primary' htmlType='submit' loading={saving}>
            {id ? 'Lưu thay đổi' : 'Tạo sản phẩm'}
          </Button>
        </Flex>
      </div>
    </Form>
  );
};

export default Desktop;